import { useMemo } from 'react';
import { isNonNullish, isNullish, unique } from 'remeda';
import { ModalStep, useModalStep } from 'src/components/ModalContainer';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { MacrosFormFields, MacrosFormModel, useSettingsMacros } from 'src/graphql';
import { useMacroFolderAssignments } from 'src/graphql/entities/record-folder-assignment';
import { useMacrosAccess } from 'src/pages/Conversations/Chat/Macros/macros-modal/hooks/use-macros-access';
import { MacrosForm } from 'src/pages/Settings/macros/macros-form';
import { useNotifications } from 'src/utils/hooks';

interface MacroEditorStepProps {
  macroId?: string;
  partnerId: number | null;
}

/** New / Edit macro: the Settings macro form, with its Save and Cancel in the step footer. */
export const MacroEditorStep = ({ macroId, partnerId }: MacroEditorStepProps) => {
  const { popStep } = useModalStep();
  const { showErrorAlert } = useNotifications();
  const { canManageInContext, canEditPlatformMacros, currentUserId, resolvedPartnerId } =
    useMacrosAccess(partnerId);

  const {
    macros,
    createMacro,
    updateMacro,
    createMacroLoading,
    updateMacroLoading,
    createMacroToAssigneeGroupLoading,
    deleteMacroToAssigneeGroupLoading,
  } = useSettingsMacros({ skip: !canManageInContext, partnerId });

  const assignmentPartnerIds = useMemo(
    () => unique([PLATFORM_PARTNER_ID, partnerId].filter(isNonNullish)),
    [partnerId],
  );
  const { setMacroFolderAssignment, isLoading: assignmentLoading } = useMacroFolderAssignments(
    {
      partnerIds: assignmentPartnerIds,
    },
  );

  const saving =
    createMacroLoading ||
    updateMacroLoading ||
    createMacroToAssigneeGroupLoading ||
    deleteMacroToAssigneeGroupLoading ||
    assignmentLoading;

  const handleSave = async (values: MacrosFormModel) => {
    if (!canManageInContext) {
      return;
    }

    const existingMacro = macroId
      ? (macros.find((macro) => macro.id === macroId) ?? null)
      : null;
    const targetPartnerId = isNullish(existingMacro?.partnerId)
      ? resolvedPartnerId
      : existingMacro.partnerId;
    const isOwner = Boolean(currentUserId && existingMacro?.createdBy === currentUserId);

    if (targetPartnerId === PLATFORM_PARTNER_ID && !canEditPlatformMacros && !isOwner) {
      showErrorAlert('Platform macros can only be edited by platform admins.');
      return;
    }

    const folderId = values[MacrosFormFields.RecordFolderId]?.id || null;
    const title = values[MacrosFormFields.Name];

    try {
      if (macroId) {
        await updateMacro(macroId, values, targetPartnerId);
        await setMacroFolderAssignment({
          macroId,
          folderId,
          title,
          partnerId: targetPartnerId,
        });
      } else {
        const createdMacroId = await createMacro(values);
        if (!createdMacroId) {
          return;
        }
        await setMacroFolderAssignment({
          macroId: createdMacroId,
          folderId,
          title,
          partnerId: resolvedPartnerId,
        });
      }
      popStep();
    } catch (error) {
      console.error(error);
      showErrorAlert('Failed to save macro. Please try again.');
    }
  };

  return (
    <MacrosForm
      macroId={macroId}
      contextPartnerId={partnerId}
      onSaveMacro={(values) => handleSave(values)}
      renderFrame={({ body, canSave, save }) => (
        <ModalStep
          title={macroId ? 'Edit macro' : 'New macro'}
          actions={[
            { label: 'Cancel', kind: 'secondary', disabled: saving },
            {
              label: macroId ? 'Save macro' : 'Create macro',
              kind: 'primary',
              onClick: save,
              disabled: !canSave,
              loading: saving,
              testId: 'macros-modal-editor-save',
            },
          ]}
        >
          {body}
        </ModalStep>
      )}
    />
  );
};
